/**
 * `styled(motion.x)` with the right prop filter.
 *
 * Emotion forwards EVERY prop to a component target (it only filters for
 * string tags), and a motion component forwards everything it does not
 * recognise to the DOM. Between them a transient `$open` prop ends up as an
 * unknown attribute and React warns on every render. This helper hands emotion
 * a filter that keeps motion's own props (`variants`, `layout`, `exit`, …) and
 * valid DOM attributes, and drops the rest.
 */
import styled from '@emotion/styled';
import isPropValid from '@emotion/is-prop-valid';
import { isValidMotionProp, motion } from 'framer-motion';

const shouldForwardProp = (prop: string): boolean =>
  isValidMotionProp(prop) || isPropValid(prop);

const options = { shouldForwardProp };

/** Motion-capable styled primitives. `m.div`…`` works exactly like `styled.div`. */
export const m = {
  div: styled(motion.div, options),
  span: styled(motion.span, options),
  section: styled(motion.section, options),
  article: styled(motion.article, options),
  aside: styled(motion.aside, options),
  nav: styled(motion.nav, options),
  header: styled(motion.header, options),
  footer: styled(motion.footer, options),
  main: styled(motion.main, options),
  form: styled(motion.form, options),
  ul: styled(motion.ul, options),
  ol: styled(motion.ol, options),
  li: styled(motion.li, options),
  p: styled(motion.p, options),
  button: styled(motion.button, options),
  a: styled(motion.a, options),
  tr: styled(motion.tr, options),
  tbody: styled(motion.tbody, options),
  label: styled(motion.label, options),
  img: styled(motion.img, options),
};

export { shouldForwardProp as motionShouldForwardProp };
